type AiCategoryEntry = readonly [
  string,
  {
    fieldName: string
  },
  ...unknown[],
];

type AiFilterValueEntry = readonly [
  string,
  {
    fieldValue: string
  },
  ...unknown[],
];

const AI_FILTER_FIELD_ORDER = [
  'Clinical Trial',
  'Conference',
  'Author',
  'Year',
  'Journal',
  'Study Phase',
];

const AI_FILTER_FIELD_INDEX = AI_FILTER_FIELD_ORDER.reduce<Record<string, number>>((acc, fieldName, index) => {
  acc[fieldName] = index;
  return acc;
}, {});

const getNumericValue = (value: string): number | undefined => {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return undefined;
  }

  const numericValue = Number(trimmedValue);

  return Number.isFinite(numericValue) ? numericValue : undefined;
};

const compareAiFilterValueEntries = (a: AiFilterValueEntry, b: AiFilterValueEntry): number => {
  const aValue = a[1].fieldValue;
  const bValue = b[1].fieldValue;
  const aNumericValue = getNumericValue(aValue);
  const bNumericValue = getNumericValue(bValue);

  if (aNumericValue !== undefined && bNumericValue !== undefined) {
    return bNumericValue - aNumericValue;
  }

  return aValue.localeCompare(bValue, undefined, { sensitivity: 'base' });
};

export const sortAiFilterValueEntries = <TEntry extends AiFilterValueEntry>(
  entries: readonly TEntry[],
): TEntry[] => [...entries].sort(compareAiFilterValueEntries);

export const sortAiCategoryEntries = <TEntry extends AiCategoryEntry>(entries: readonly TEntry[]): TEntry[] => {
  return entries
    .map((entry, index) => ({ entry, index }))
    .sort((a, b) => {
      const aOrder = AI_FILTER_FIELD_INDEX[a.entry[1].fieldName] ?? Number.MAX_SAFE_INTEGER;
      const bOrder = AI_FILTER_FIELD_INDEX[b.entry[1].fieldName] ?? Number.MAX_SAFE_INTEGER;

      if (aOrder !== bOrder) {
        return aOrder - bOrder;
      }

      return a.index - b.index;
    })
    .map(({ entry }) => entry);
};
